import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FormData } from "../AdvisorWizard";
import { getRecommendations } from "@/utils/recommendations";
import { ArrowLeft, CheckCircle, Phone, Mail, Calendar, Award, TrendingUp, Shield, AlertCircle } from "lucide-react";

interface ResultsStepProps {
  data: FormData;
  onStartOver: () => void;
  onBack: () => void;
}

const ResultsStep = ({ data, onStartOver, onBack }: ResultsStepProps) => {
  const recommendations = getRecommendations(data);
  
  const handlePhoneCall = () => {
    window.open('tel:089628246446', '_self');
  };

  const handleEmail = () => {
    window.open("mailto:info@renteplusimmobilie.de?subject=Beratungsanfrage%20Immobilienverrentung", "_self");
  };

  const handleCalendly = () => {
    window.open("https://calendly.com/renteplusimmobilie/20min", "_blank");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="card-elevated bg-gradient-subtle">
        <CardHeader className="text-center pb-6">
          <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
            <CheckCircle className="h-12 w-12 text-accent" />
          </div>
          <CardTitle className="text-3xl md:text-4xl text-primary mb-4">
            Ihre persönlichen Empfehlungen
          </CardTitle>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Basierend auf Ihren Angaben haben wir {recommendations.length} passende {recommendations.length === 1 ? 'Option' : 'Optionen'} für Sie gefunden.
          </p>
        </CardHeader>
      </Card>

      {/* Eingaben Zusammenfassung */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-xl text-primary flex items-center gap-2">
            <Award className="h-6 w-6" />
            Ihre Angaben im Überblick
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-primary">Persönlich</h4>
              <p className="text-sm text-muted-foreground">Alter: {data.age}</p>
              <p className="text-sm text-muted-foreground">Status: {data.familyStatus}</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-primary">Immobilie</h4>
              <p className="text-sm text-muted-foreground">Typ: {data.propertyType}</p>
              <p className="text-sm text-muted-foreground">Wert: {data.estimatedValue}</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-primary">Präferenzen</h4>
              <p className="text-sm text-muted-foreground">Priorität: {data.priority}</p>
              <p className="text-sm text-muted-foreground">Auszahlung: {data.paymentForm}</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-primary">Zukunft</h4>
              <p className="text-sm text-muted-foreground">{data.futurePlans}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Empfehlungen */}
      <div className="space-y-6">
        {recommendations.map((recommendation, index) => (
          <Card 
            key={recommendation.id} 
            className={`card-elevated ${recommendation.isRecommended ? 'border-accent shadow-accent/20' : ''}`}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Badge variant={recommendation.isRecommended ? "default" : "secondary"} className="text-sm">
                      {recommendation.match}% Übereinstimmung
                    </Badge>
                    {recommendation.isRecommended && (
                      <Badge variant="outline" className="text-accent border-accent">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Top-Empfehlung
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-2xl text-primary">
                    {index + 1}. {recommendation.title}
                  </CardTitle>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {recommendation.description}
                  </p>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vorteile */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent" />
                    Vorteile für Sie
                  </h4>
                  <ul className="space-y-2">
                    {recommendation.advantages.map((advantage, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                        <span className="text-sm">{advantage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Nachteile */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-muted-foreground" />
                    Zu beachten
                  </h4>
                  <ul className="space-y-2">
                    {recommendation.disadvantages.map((disadvantage, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 mt-2 flex-shrink-0" />
                        <span className="text-sm">{disadvantage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Nächste Schritte */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="font-semibold text-primary mb-2">Nächste Schritte:</h4>
                <p className="text-muted-foreground text-sm">
                  {(() => {
                    const text = recommendation.nextSteps;
                    const clickablePatterns = [
                      'kostenlosen Beratungstermin',
                      'Lassen Sie uns gemeinsam berechnen',
                      'berechnen Ihre individuelle Rentenhöhe',
                      'berechnen Ihre monatliche Miete',
                      'zeigen Ihnen, wie hoch Ihre Einmalzahlung',
                      'prüfen Ihre Bonität'
                    ];
                    
                    let formattedText = text;
                    clickablePatterns.forEach(pattern => {
                      const regex = new RegExp(`(${pattern}[^.]*\\.?)`, 'gi');
                      formattedText = formattedText.replace(regex, '|CLICKABLE|$1|/CLICKABLE|');
                    });
                    
                    return formattedText.split('|').map((part, index) => {
                      if (part === 'CLICKABLE') return null;
                      if (part === '/CLICKABLE') return null;
                      
                      const prevPart = formattedText.split('|')[index - 1];
                      if (prevPart === 'CLICKABLE') {
                        return (
                          <button
                            key={index}
                            onClick={handleCalendly}
                            className="text-accent hover:text-accent-hover underline underline-offset-2 cursor-pointer font-bold transition-colors"
                          >
                            {part}
                          </button>
                        );
                      }
                      
                      return <span key={index}>{part}</span>;
                    }).filter(Boolean);
                  })()}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Call to Action */}
      <Card className="card-elevated bg-gradient-subtle">
        <CardContent className="text-center p-8">
          <h3 className="text-2xl font-bold text-primary mb-4">
            Bereit für den nächsten Schritt?
          </h3>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Unsere Experten beraten Sie gerne persönlich und unverbindlich zu Ihren Optionen. 
            Vereinbaren Sie jetzt Ihren kostenlosen Beratungstermin.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button variant="accent" size="lg" onClick={handleCalendly} className="text-lg px-8">
              <Calendar className="h-5 w-5 mr-2" />
              Termin vereinbaren
            </Button>
            <Button variant="outline" size="lg" onClick={handlePhoneCall} className="text-lg px-8">
              <Phone className="h-5 w-5 mr-2" />
              089 62 82 46 46
            </Button>
            <Button variant="outline" size="lg" onClick={handleEmail} className="text-lg px-8">
              <Mail className="h-5 w-5 mr-2" />
              E-Mail senden
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground">
            ✅ 100% kostenlos • ✅ Unverbindlich • ✅ Persönliche Betreuung
          </p>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between pt-6">
        <Button variant="ghost" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Zurück zu Schritt 3
        </Button>
        
        <Button variant="outline" onClick={onStartOver}>
          Neue Berechnung starten
        </Button>
      </div>
    </div>
  );
};

export default ResultsStep;